import { SavedQuickListItem } from "../SavedQuickListsPanel.previous";
import { getRecipesAsyncUsingRecipeId, getTitleDescriptionTemplateByIdAsync, getCategoryTemplateUsingTemplateIdAsync, getSelectedCategoryTreeAsync, getMoreMappingByTemplateIdAsync } from "src/redux/actions/templateAndPricingActions";
import { getPriceTemplateByIdAsync } from "src/redux/actions/productPricingAction";
import { PriceRuleFormValue } from "../hooks/usePriceModifierFields.previous";
import {
  fieldIdFromMoreMappingTemplate,
  keepMoreMappingRefs,
  type MoreMappingIdRef,
} from "../../../components/utils/quickListMoreMappingIdentity";
import {
  isUsablePriceHydratePayload,
  mapPriceTemplateToQuickListForm,
  mergePriceHydratePayloads,
  needsPriceTemplateDetailFetch,
  resolvePriceTemplateId,
} from "./quickListPriceHydrate.previous";

/** Resolves platform field id from list/detail mapping rows (id or nested object). */
export const platformFieldIdFromMapping = (
  field: unknown,
  fallback = ""
): string => {
  if (field == null || field === "") return fallback;
  if (typeof field === "object" && field !== null && "id" in field) {
    const id = (field as { id?: number | string }).id;
    return id != null && id !== "" ? String(id) : fallback;
  }
  return String(field);
};

export interface HydrateFormContext {
  platformId: number | undefined;
  dispatch: any;
  checkFormField: string | undefined;
  priceTemplateFields: any[];
  // Conditions ΓÇö written into state so Formik can receive them via initialValues on remount
  setHydratedProductRecipes: (rows: any[]) => void;
  setHydratedDataConjunction: (val: string) => void;
  setEditMappingDataDetails: (rows: any[]) => void;
  setEditingRecipeId: (id: number | null) => void;
  setRuleName: (name: string) => void;
  setStatus?: (status: "ACTIVE" | "DRAFT") => void;
  setEditSubTemplateIds: (ids: any) => void; // useState setter ΓÇö also accepts (prev) => next
  setHydratingTitle: (state: boolean) => void;
  setTitleInput: (val: string) => void;
  setEditorData: (val: string) => void;
  setTagsInput: (val: string) => void;
  setSkuInput: (val: string) => void;
  setTitleAdvanceSetting: (settings: any) => void;
  setDescriptionAdvanceSetting: (settings: any) => void;
  setOpenTitleAdvanceConfig: (state: boolean) => void;
  setOpenDescriptionAdvanceConfig: (state: boolean) => void;
  setHydratingPrice: (state: boolean) => void;
  setPriceRules: (rules: PriceRuleFormValue[]) => void;
  setRepricing: (repricing: any) => void;
  setPriceAdjustmentType: (type: string) => void;
  setHydratingCategory: (state: boolean) => void;
  setCategoryUpdateData: (data: any) => void;
  setInitialCategoryState: (state: any) => void;
  setEngineState: (state: any) => void;
  setEngineKey: (key: any) => void;
  setSelectedAttributes: (attrs: any[]) => void;
  setHydratingMoreMappings: (state: boolean) => void;
  setMoreFieldMappingData: (updater: any) => void;
  onPriceTemplateLoaded?: (payload: any) => void;
}

export const hydrateQuickListFormForEdit = async (
  savedItem: SavedQuickListItem,
  context: HydrateFormContext
) => {
  const {
    platformId,
    dispatch,
    checkFormField,
    priceTemplateFields,
    setHydratedProductRecipes,
    setHydratedDataConjunction,
    setEditMappingDataDetails,
    setEditingRecipeId,
    setRuleName,
    setStatus,
    setEditSubTemplateIds,
    setHydratingTitle,
    setTitleInput,
    setEditorData,
    setTagsInput,
    setSkuInput,
    setTitleAdvanceSetting,
    setDescriptionAdvanceSetting,
    setOpenTitleAdvanceConfig,
    setOpenDescriptionAdvanceConfig,
    setHydratingPrice,
    setPriceRules,
    setRepricing,
    setPriceAdjustmentType,
    setHydratingCategory,
    setCategoryUpdateData,
    setInitialCategoryState,
    setEngineState,
    setEngineKey,
    setSelectedAttributes,
    setHydratingMoreMappings,
    setMoreFieldMappingData,
    onPriceTemplateLoaded,
  } = context;

  // 1. Conditions ΓÇö MUST be set in the same synchronous batch as setEditingRecipeId.
  // The Formik key depends on editingRecipeId, so when it changes Formik remounts.
  // If we set conditions AFTER an await, the await breaks React batching and Formik
  // remounts with stale default values before setHydratedProductRecipes fires.
  // These only need savedItem.mapping_data (already available, no API call needed).
  const mappedRecipes = (savedItem.mapping_data && savedItem.mapping_data.length > 0)
    ? savedItem.mapping_data.map((d: any) => ({
        id: d.id ?? null,
        checkPlatformField: platformFieldIdFromMapping(d.check_platform_field, checkFormField || ""),
        checkOperator: d.check_operator ?? "CONTAINS",
        checkConjunction: d.check_conjunction ?? "OR",
        checkValue: d.check_value ?? "",
      }))
    : [{ id: null, checkConjunction: 'OR', checkOperator: 'CONTAINS', checkPlatformField: checkFormField || '', checkValue: '' }];

  setHydratedDataConjunction(savedItem.check_data_conjunction ?? "OR");
  setHydratedProductRecipes(mappedRecipes);
  setEditMappingDataDetails(savedItem.mapping_data ?? []);
  setEditingRecipeId(savedItem.id);
  setRuleName(savedItem.name ?? "");
  if (setStatus) {
    setStatus(savedItem.status ?? "ACTIVE");
  }
  // All four setState calls above are in the same synchronous block ΓÇö React 18
  // batches them into one render, so Formik remounts with the correct initialValues.

  // Fetch full recipe details to get complete sub-template relationships (e.g. other_templates nested structure)
  let fullRecipeDetail: any = null;
  try {
    const recipeRes: any = await dispatch(
      getRecipesAsyncUsingRecipeId({ recipeId: savedItem.id + "", otherParams: {} })
    );
    if (recipeRes.payload) {
      fullRecipeDetail = recipeRes.payload;
    }
  } catch (err) {
    console.error("Failed to fetch full recipe detail:", err);
  }

  if (fullRecipeDetail?.mapping_data?.length) {
    setEditMappingDataDetails(fullRecipeDetail.mapping_data);
  }

  const otherTemplates = fullRecipeDetail?.other_templates ?? savedItem.other_templates ?? [];
  const moreMappingRefs = keepMoreMappingRefs(otherTemplates);
  const recipePrice = fullRecipeDetail?.price_template ?? savedItem.price_template ?? null;
  const priceId = resolvePriceTemplateId(recipePrice);

  // Store sub-template IDs ΓÇö keep templateId even when fieldId is missing.
  setEditSubTemplateIds({
    titleId: savedItem.title_description_template ?? null,
    priceId,
    categoryId: savedItem.category_template ?? null,
    moreMappingIds: moreMappingRefs,
  });

  // 2. Title/Description
  if (savedItem.title_description_template) {
    setHydratingTitle(true);
    dispatch(
      getTitleDescriptionTemplateByIdAsync({
        templateId: savedItem.title_description_template,
      })
    )
      .then((res: any) => {
        const titleData = res.payload;
        if (titleData) {
          setTitleInput(titleData.title_value_str ?? titleData.title_value_string ?? titleData.title ?? "");
          setEditorData(
            titleData.description_value_str
              ?? titleData.description_value_string
              ?? titleData.description
              ?? ""
          );
          setTagsInput(titleData.tags_value_str ?? titleData.tags_value_string ?? titleData.tags ?? "");
          setSkuInput(titleData.sku_value_str ?? titleData.sku_value_string ?? titleData.sku ?? "");
          if (titleData.title_processing_config) {
            const hasTitleAi = !!titleData.title_processing_config.isRemoveHtml || 
                               !!titleData.title_processing_config.isTranslate || 
                               !!titleData.title_processing_config.isRephrasing;
            setOpenTitleAdvanceConfig(hasTitleAi);
            setTitleAdvanceSetting({
              isRemoveHtml: !!titleData.title_processing_config.isRemoveHtml,
              isTranslate: !!titleData.title_processing_config.isTranslate,
              translate_language: titleData.title_processing_config.translate_language ?? "",
              isRephrasing: !!titleData.title_processing_config.isRephrasing,
              rephrasing_limit: titleData.title_processing_config.rephrasing_limit ?? "",
            });
          }
          if (titleData.description_processing_config) {
            const hasDescAi = !!titleData.description_processing_config.isRemoveHtml || 
                              !!titleData.description_processing_config.isTranslate || 
                              !!titleData.description_processing_config.isRephrasing;
            setOpenDescriptionAdvanceConfig(hasDescAi);
            setDescriptionAdvanceSetting({
              isRemoveHtml: !!titleData.description_processing_config.isRemoveHtml,
              isTranslate: !!titleData.description_processing_config.isTranslate,
              translate_language: titleData.description_processing_config.translate_language ?? "",
              isRephrasing: !!titleData.description_processing_config.isRephrasing,
              rephrasing_limit: titleData.description_processing_config.rephrasing_limit ?? "",
            });
          }
        }
      })
      .catch((err) => {
        console.error("Failed to hydrate title/description:", err);
      })
      .finally(() => setHydratingTitle(false));
  }

  // 3. Price ΓÇö prefer template-mapping.price_template; detail GET only for multi-row rules.
  const applyPriceHydrate = (payload: any) => {
    if (!isUsablePriceHydratePayload(payload)) return;
    onPriceTemplateLoaded?.(payload);
    const mapped = mapPriceTemplateToQuickListForm(payload, priceTemplateFields);
    setRepricing(mapped.repricing);
    setPriceAdjustmentType(mapped.adjustmentType);
    if (mapped.rules.length > 0) {
      setPriceRules(mapped.rules);
    }
  };

  applyPriceHydrate(recipePrice);

  if (needsPriceTemplateDetailFetch(recipePrice, priceId) && platformId && priceId) {
    setHydratingPrice(true);
    dispatch(
      getPriceTemplateByIdAsync({
        templateId: priceId,
        platformId,
      })
    )
      .then((res: any) => {
        const detail = Array.isArray(res.payload)
          ? res.payload
          : res.payload?.results ?? res.payload;
        applyPriceHydrate(mergePriceHydratePayloads(recipePrice, detail));
      })
      .catch((err) => {
        console.error("Failed to hydrate price rules:", err);
      })
      .finally(() => setHydratingPrice(false));
  }

  // 4. Category
  if (savedItem.category_template) {
    setHydratingCategory(true);
    dispatch(
      getCategoryTemplateUsingTemplateIdAsync({
        category_temp_id: savedItem.category_template,
      })
    )
      .then(async (res: any) => {
        const catData = res.payload;
        setCategoryUpdateData(catData);
        if (catData?.category_data) {
          const cData = catData.category_data;
          const cId = cData?.id ?? cData;

          let fullPath = cData?.api_name ?? cData?.display_name ?? cData?.name ?? "";
          try {
            const treeRes = await dispatch(getSelectedCategoryTreeAsync({ category_data_id: cId }));
            if ((treeRes as any).payload?.length) {
              fullPath = (treeRes as any).payload.map((it: any) => it.api_name).join(' > ');
            }
          } catch (err) {
            console.error("Failed to fetch full category path:", err);
          }

          setInitialCategoryState({
            categoryId: cId,
            categoryPath: fullPath,
            platformFieldId: cData?.platform_field ?? catData?.platform_field?.id ?? null
          });
          setEngineState({
            categoryId: cId,
            categoryPath: fullPath,
            isLeafCategory: true,
            showAttributes: false,
            isAttributeLoading: false,
          });
          setEngineKey((prev: any) => prev + 1);
          if (catData.attributes && Array.isArray(catData.attributes)) {
            setSelectedAttributes(catData.attributes);
          }
        }
      })
      .catch((err) => {
        console.error("Failed to hydrate category mapping:", err);
      })
      .finally(() => setHydratingCategory(false));
  }

  // 5. More Mappings ΓÇö always fetch by saved template id (reliable vs paginated list).
  if (moreMappingRefs.length > 0 && platformId) {
    setHydratingMoreMappings(true);
    try {
      const results = await Promise.allSettled(
        moreMappingRefs.map((ref) =>
          dispatch(
            getMoreMappingByTemplateIdAsync({ templateId: ref.templateId })
          ).then((res: any) => {
            const found = res.payload;
            return {
              fieldId: ref.fieldId ?? fieldIdFromMoreMappingTemplate(found),
              templateId: ref.templateId,
              moreMappingData: found?.id ? found : null,
            };
          })
        )
      );

      const fulfilled = results
        .filter((r) => r.status === "fulfilled" && r.value !== null)
        .map((r: any) => (r as any).value as {
          fieldId: number | null
          templateId: number
          moreMappingData: any
        })
        .filter((item) => item.moreMappingData);

      const resolvedRefs: MoreMappingIdRef[] = fulfilled
        .filter((item) => item.templateId)
        .map((item) => ({
          fieldId: item.fieldId,
          templateId: item.templateId,
        }));

      setEditSubTemplateIds((prev: any) => ({
        ...prev,
        moreMappingIds: resolvedRefs.length ? resolvedRefs : moreMappingRefs,
      }));

      setMoreFieldMappingData((prevList: any[]) =>
        prevList.map((item) => {
          const match = fulfilled.find(
            (f) =>
              (f.fieldId != null && Number(f.fieldId) === Number(item.id)) ||
              Number(f.moreMappingData?.platform_field?.id) === Number(item.id) ||
              Number(f.moreMappingData?.platform_field) === Number(item.id)
          );
          return match?.moreMappingData
            ? { ...item, moreMappingData: match.moreMappingData }
            : item;
        })
      );
    } catch (err) {
      console.error("Failed to hydrate more mappings:", err);
    } finally {
      setHydratingMoreMappings(false);
    }
  }

  window.scrollTo({ top: 0, behavior: "smooth" });
};
